import { useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTour, type TourStep } from '@shared/tour/Tour';
import { supabase } from '@shared/supabase/client';
import { useSupabaseQuery, useInvalidateSupabase } from '@shared/supabase/hooks';
import { useAuth } from '@shared/auth/AuthContext';
import { LanguageSwitcher } from '@shared/i18n/LanguageSwitcher';
import { NamedIcon } from '@shared/ui/icons';
import { InstallAppBanner, InstallInterstitial, Footer, IconDefs } from '@shared/ui';
import { Avatar } from '@shared/ui/Avatar';
import { SupportWidget, openSupport } from '@shared/support/SupportWidget';
import { useUnreadThreads } from '@shared/hooks/useUnreadThreads';
import { useCreator } from './useCreator';
import CreatorClaim from './CreatorClaim';
import NichePrompt from './NichePrompt';
import './creator.css';

// Creator app shell: gate on claim status (unverified → claim screen), else a mobile layout
// with a top bar + bottom nav (≥900px: the nav moves into the top bar). Verified screens render into <Outlet>.
export default function CreatorApp() {
  const { t } = useTranslation(['provider', 'common']);
  const { user, signOut } = useAuth();
  const { provider, verified, pendingClaim, isLoading, error, refetch } = useCreator();
  const { total: unreadTotal } = useUnreadThreads();
  const location = useLocation();
  const navigate = useNavigate();
  const invalidate = useInvalidateSupabase();

  // Post-claim install nudge: right after a creator confirms their account (CreatorClaim
  // sets the sessionStorage flag), show the full-screen install prompt once — peak intent.
  const [showInstall, setShowInstall] = useState(false);
  // Niche: asked once per session right after verification while none is set (skippable).
  const [nicheSkipped, setNicheSkipped] = useState(() => {
    try { return sessionStorage.getItem('adamar_niche_skip') === '1'; } catch { return false; }
  });
  // Niche is saved to the creator's own row, which needs a verified claim.
  const needNiche = !!provider && verified && !(provider.niche_ids?.length) && !nicheSkipped;
  useEffect(() => {
    try {
      if (provider && sessionStorage.getItem('adamar_just_claimed') === '1') {
        sessionStorage.removeItem('adamar_just_claimed');
        setShowInstall(true);
      }
    } catch { /* private mode / storage disabled */ }
  }, [provider]);

  // Self-serve account deletion — tucked into the support panel («?»), not the Profile tab,
  // so it can't be tapped by accident. Removes the login only; scraped data stays.
  const deleteAccount = async () => {
    if (!confirm(t('provider:creator.profile.deleteConfirm', { defaultValue: 'Удалить ваш аккаунт? Логин и доступ будут удалены безвозвратно. Публичная статистика по вашему профилю останется в системе.' }))) return;
    const { error } = await supabase.rpc('delete_my_creator_account');
    if (error) { alert(error.message); return; }
    await signOut();
    navigate('/');
  };

  // A just-confirmed creator has never been scraped → empty dashboard. Trigger the
  // one-shot self-serve scrape on first load (no background cron). The endpoint is
  // idempotent + guarded (initial-only, per-provider lock), so this costs at most one
  // Apify run per creator regardless of how often the app mounts; a repeat is a free
  // no-op. Covers both confirm paths (own button OR admin). localStorage dedupes within
  // the browser; the server is the authority.
  const scrapeTriedRef = useRef(false);
  useEffect(() => {
    if (!provider?.id || scrapeTriedRef.current) return;
    const key = `adamar_profile_scraped_${provider.id}`;
    try { if (localStorage.getItem(key)) return; } catch { /* ignore */ }
    scrapeTriedRef.current = true;
    void (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const token = session?.access_token;
        if (!token) { scrapeTriedRef.current = false; return; }
        const r = await fetch('/api/scrape-my-profile', { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
        const j = await r.json().catch(() => ({}));
        // Only mark done when the server resolved it (scraped or already-scraped), so a
        // transient failure retries on the next load instead of leaving an empty dashboard.
        if (j?.ok && (j.skipped || typeof j.inserted === 'number')) {
          try { localStorage.setItem(key, '1'); } catch { /* ignore */ }
          if (j.inserted > 0) invalidate([]);
        } else {
          scrapeTriedRef.current = false;
        }
      } catch { scrapeTriedRef.current = false; }
    })();
  }, [provider?.id, invalidate]);

  // "New campaigns" badge on the Demand tab: count open campaigns published since
  // the creator last opened Demand (localStorage last-seen). Shares the feed query
  // cache with CreatorDemand, so no extra fetch. This is the cheap in-app
  // notification; real web-push is a separate build.
  const [lastSeen, setLastSeen] = useState<string>(() => {
    try { return localStorage.getItem('demand_last_seen') || ''; } catch { return ''; }
  });
  const { data: feed } = useSupabaseQuery<{ published_at: string | null }[]>(
    ['creator-campaign-feed'],
    async () => supabase.rpc('get_creator_campaign_feed'),
    { enabled: !!provider?.id, staleTime: 60_000, refetchInterval: 300_000 },
  );
  const newCampaigns = (feed ?? []).filter((r) => r.published_at && (!lastSeen || r.published_at > lastSeen)).length;

  // Collab tab badge: pending incoming proposals + unread collab messages.
  const { data: collabBadge } = useSupabaseQuery<number>(
    ['collab-badge'],
    async () => supabase.rpc('get_my_collab_badge'),
    { enabled: !!provider?.id, staleTime: 60_000, refetchInterval: 120_000 },
  );
  useEffect(() => {
    if (location.pathname === '/provider/demand') {
      const now = new Date().toISOString();
      try { localStorage.setItem('demand_last_seen', now); } catch { /* ignore */ }
      setLastSeen(now);
    }
  }, [location.pathname]);

  // If the gate read failed (flaky link), don't leave the creator stranded on
  // the error screen waiting for a manual tap — keep re-attempting on a timer
  // until the profile resolves. Cheap: the read is a single indexed row.
  useEffect(() => {
    if (!error || !user) return;
    const id = setInterval(() => refetch(), 5000);
    return () => clearInterval(id);
  }, [error, user, refetch]);

  // Guided intro tour — spotlights the bottom-nav tabs on a verified creator's
  // first visit; the "?" in the header replays it. Steps target [data-tour=…].
  const tourSteps = useMemo<TourStep[]>(() => (provider ? [
    { title: t('provider:tour.welcome.title', { defaultValue: 'Добро пожаловать в AdaMar' }),
      body: t('provider:tour.welcome.body', { defaultValue: 'Быстрый тур за 30 секунд — покажем, где что.' }) },
    { target: '[data-tour="creator-home"]',
      title: t('provider:tour.home.title', { defaultValue: 'Дом' }),
      body: t('provider:tour.home.body', { defaultValue: 'Твои охваты, ER и рост — цифры, которые видят бренды.' }) },
    { target: '[data-tour="creator-collabs"]',
      title: t('provider:tour.collabs.title', { defaultValue: 'Коллабы' }),
      body: t('provider:tour.collabs.body', { defaultValue: 'Найди блогеров своей ниши, предложи коллаб и договорись в чате.' }) },
    { target: '[data-tour="creator-demand"]',
      title: t('provider:tour.demand.title', { defaultValue: 'Спрос' }),
      body: t('provider:tour.demand.body', { defaultValue: 'Задания от брендов — откликайся в один тап.' }) },
    { target: '[data-tour="creator-requests"]',
      title: t('provider:tour.requests.title', { defaultValue: 'Отклики' }),
      body: t('provider:tour.requests.body', { defaultValue: 'Твои отклики на задания и их статус. Выбранным блогерам бренд пишет сам.' }) },
    { target: '[data-tour="creator-prices"]',
      title: t('provider:tour.prices.title', { defaultValue: 'Профиль' }),
      body: t('provider:tour.prices.body', { defaultValue: 'Услуги и цены, контакты и аккаунт.' }) },
    { title: t('provider:tour.final.title', { defaultValue: 'Всё, ты готов!' }),
      body: t('provider:tour.final.body', { defaultValue: 'Пройти тур снова можно из кнопки помощи «?» вверху справа.' }) },
  ] : []), [provider, t]);
  const tour = useTour({
    id: `creator_${user?.id ?? 'x'}`,
    steps: tourSteps,
    // Require a real user id so the localStorage key (adamar_tour_creator_<id>) is stable —
    // otherwise the tour could arm under the 'x' fallback and re-show once user resolves.
    enabled: !!provider && !!user?.id,
    labels: {
      skip: t('provider:tour.skip', { defaultValue: 'Пропустить' }),
      back: t('provider:tour.back', { defaultValue: 'Назад' }),
      next: t('provider:tour.next', { defaultValue: 'Далее' }),
      done: t('provider:tour.done', { defaultValue: 'Погнали' }),
    },
  });

  // A new section starts at the top — without this a detail page opened from far down
  // «Дом» kept «Дом»'s scroll offset. Back (POP) keeps the browser's own restoration;
  // hash links (#creator-peers) scroll themselves.
  const navType = useNavigationType();
  useEffect(() => {
    if (navType !== 'POP' && !location.hash) window.scrollTo(0, 0);
  }, [location.pathname, location.hash, navType]);

  // «Дом» stays highlighted on its detail pages (stats, brands, top reels).
  const HOME_SUB = ['/provider/stats', '/provider/brands', '/provider/top-reels'];
  const nav = (to: string, icon: string, label: string, end?: boolean, badge?: number, tourId?: string) => (
    <NavLink to={to} end={end} data-tour={tourId}>
      {({ isActive }) => {
        const on = isActive || (to === '/provider' && HOME_SUB.includes(location.pathname));
        return (
          <div className={on ? 'cr-nav-item on' : 'cr-nav-item'}>
            <div style={{ position: 'relative', display: 'flex' }}>
              <NamedIcon name={icon} size={20} color={on ? '#4a9eff' : '#667'} />
              {!!badge && (
                <span style={{ position: 'absolute', top: -6, right: -9, background: '#e74c3c', color: '#fff', borderRadius: 8, fontSize: 10, fontWeight: 700, lineHeight: '14px', minWidth: 14, height: 14, textAlign: 'center', padding: '0 3px' }}>
                  {badge > 9 ? '9+' : badge}
                </span>
              )}
            </div>
            <span className="cr-nav-label">{label}</span>
          </div>
        );
      }}
    </NavLink>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#0b0d12', color: 'var(--text)' }}>
      {/* Mount once so gradient-stroked NamedIcons (no explicit color) render. */}
      <IconDefs />
      <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', paddingTop: 'calc(12px + env(safe-area-inset-top))', borderBottom: '1px solid rgba(255,255,255,0.06)', position: 'sticky', top: 0, background: 'rgba(11,13,18,0.92)', backdropFilter: 'blur(8px)', zIndex: 10 }}>
        <div style={{ fontWeight: 700, color: '#fff' }}>Ada<span style={{ color: '#8a5cff' }}>Mar</span></div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {provider && (
            <button type="button" onClick={() => navigate('/provider/prices')}
              aria-label={t('provider:creator.nav.profile', { defaultValue: 'Профиль' })}
              style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', display: 'flex', borderRadius: '50%' }}>
              <Avatar src={provider.profile_pic_mirrored_url} name={provider.handle ?? '?'} size={30} />
            </button>
          )}
          {/* Help lives in the header: a floating «?» covered the right-hand tiles on a phone. */}
          <button type="button" onClick={openSupport} aria-label={t('common:support.open', { defaultValue: 'Помощь' })}
            style={{ width: 32, height: 32, borderRadius: '50%', border: '1px solid rgba(255,255,255,0.18)', background: 'transparent', color: 'var(--text)', fontWeight: 700, fontSize: 15, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>?</button>
          <LanguageSwitcher compact />
        </div>
      </header>

      <InstallAppBanner />

      {isLoading ? (
        <div style={{ color: 'var(--dim)', textAlign: 'center', padding: 40 }}>…</div>
      ) : provider ? (
        <>
          <main id="main" tabIndex={-1} className="cr-main">
            {!verified && location.pathname !== '/provider/prices' && (
              <div role="status" style={{ maxWidth: 1040, margin: '8px auto 0', padding: '0 14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', background: 'rgba(74,158,255,0.08)', border: '1px solid rgba(74,158,255,0.3)', borderRadius: 12, padding: '10px 12px' }}>
                  <span style={{ flex: 1, minWidth: 200, fontSize: 13, color: 'var(--text-2)', lineHeight: 1.45 }}>
                    {t('provider:creator.verify.banner', { defaultValue: 'Аккаунт не подтверждён. Смотреть статистику можно уже сейчас, а откликаться на задания и предлагать коллабы — после подтверждения.' })}
                  </span>
                  <button type="button" className="btn btn-primary" style={{ minHeight: 40, padding: '0 14px', fontSize: 14 }}
                    onClick={() => navigate('/provider/prices#verify')}>
                    {t('provider:creator.verify.cta', { defaultValue: 'Подтвердить' })}
                  </button>
                </div>
              </div>
            )}
            {needNiche
              ? <NichePrompt providerId={provider.id} onDone={() => { setNicheSkipped(true); refetch(); }} />
              : <><Outlet /><Footer /></>}
          </main>
          <nav className="cr-nav" aria-label={t('provider:creator.nav.label', { defaultValue: 'Разделы кабинета' })}>
            {nav('/provider', 'home', t('provider:creator.nav.home', { defaultValue: 'Дом' }), true, undefined, 'creator-home')}
            {nav('/provider/collabs', 'handshake', t('provider:creator.nav.collabs', { defaultValue: 'Коллабы' }), false, collabBadge ?? undefined, 'creator-collabs')}
            {nav('/provider/demand', 'megaphone', t('provider:creator.nav.demand', { defaultValue: 'Спрос' }), false, newCampaigns, 'creator-demand')}
            {nav('/provider/requests', 'send', t('provider:creator.nav.requests', { defaultValue: 'Отклики' }), false, unreadTotal, 'creator-requests')}
            {nav('/provider/prices', 'user', t('provider:creator.nav.profile', { defaultValue: 'Профиль' }), false, undefined, 'creator-prices')}
          </nav>
        </>
      ) : error ? (
        <div style={{ color: 'var(--muted)', textAlign: 'center', padding: '56px 28px' }}>
          <div style={{ fontSize: 14, lineHeight: 1.5, marginBottom: 14 }}>{t('provider:creator.loadError', { defaultValue: 'Не удалось загрузить ваш профиль. Проверьте соединение и попробуйте снова.' })}</div>
          <button type="button" onClick={refetch} style={{ background: 'rgba(74,158,255,0.16)', border: '1px solid rgba(74,158,255,0.45)', color: 'var(--accent-text)', borderRadius: 8, padding: '9px 18px', fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' }}>{t('provider:creator.retry', { defaultValue: 'Обновить' })}</button>
        </div>
      ) : (
        <main><CreatorClaim pending={pendingClaim} onRefetch={refetch} /></main>
      )}
      {!provider && <div style={{ textAlign: 'center', color: 'var(--dim)', fontSize: 11, padding: '10px 0 24px' }}>{user?.email}</div>}
      <SupportWidget fab={false} onReplayTour={provider ? tour.start : undefined} onDeleteAccount={provider ? deleteAccount : undefined} />
      {tour.render}
      {showInstall && <InstallInterstitial onClose={() => setShowInstall(false)} />}
    </div>
  );
}
